import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useSession, useSupabaseClient } from "@supabase/auth-helpers-react";
import { useRef, useState } from "react";

// --- CHANGED: Renamed imports for clarity and consistency ---
import { buildStrategistInstructions } from "../utils/StrategistInstructions";
import { runProvocationEngine } from "../utils/provocationEngine.js";//import strategistInstructionsDiagnose from "../utils/StrategistInstructionsDiagnose";
//import strategistInstructionsDisrupt from "../utils/StrategistInstructionsDisrupt";
//import strategistInstructionsExploreAlt from "../utils/StrategistInstructionsExploreAlt";
//import strategistInstructionsValidate from "../utils/StrategistInstructionsValidateAssumptions";
import strategistInstructionsBlackSwan from "../utils/StrategistInstructionsBlackSwan";
// Renamed files to better reflect their purpose
import strategistInstructionsInnovationCatalyst from "../utils/StrategistInstructionsInnovationCatalyst";
import strategistInstructionsAdaptive from "../utils/StrategistInstructionsAdaptive";
import StrategistInstructionsAgentic from "../utils/StrategistInstructionsAgentic";
// --- Configuration Constants for Resilient Sending ---
const MAX_HISTORY = 8;
const COMPACT_HISTORY = 4;

// --- Agent Mode defaults (wire to a UI toggle later if you want) ---
const AGENT_MODE_DEFAULTS = {
  enabled: true,
  allowWebSearch: true,
};


// === ZAPIER: quick + dirty client-side relay (ok for testing) ===

// Pull the last fenced JSON block from a message (```json ... ``` or ``` ... ```)
function extractActionPlanJSON(text) {
  if (!text) return null;
  const fenced = text.match(/```json([\s\S]*?)```/i) || text.match(/```([\s\S]*?)```/);
  if (!fenced) return null;
  try {
    return JSON.parse(fenced[1].trim());
  } catch {
    return null;
  }
}

 async function sendToZapier(actionPlan, extras = {}, authToken = "") {
   try {

     const res = await fetch(`${"https://vintariai-backend-1.onrender.com"}/api/relay/zapier`, {
       method: "POST",
       headers: {
         "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
       },
       body: JSON.stringify({ actionPlan, ...extras }),
     });
     return res.ok;
   } catch {
     return false;
   }
}







// Decide when live web search is useful
function shouldUseWebSearch({ input, primaryIntent }) {
const timeWords = /\b(today|this week|this month|latest|recent|20\d{2}|update|news)\b/i;
  const strategicIntents = ["Market", "Competitor", "Policy", "Risk", "Horizon", "BlackSwan"];
  return (
    timeWords.test(input) ||
    strategicIntents.includes(primaryIntent || "") ||
    /\b(price|launch|funding|acquired|acquisition|regulation|bill|policy|breach|cve|benchmark)\b/i.test(input)
  );
}

function buildTools({ allowWebSearch, useWebSearch }) {
  const tools = [];
  if (allowWebSearch && useWebSearch) tools.push({ type: "web_search" });
  return tools;
}


/**
 * Builds the payload for the API request, trimming history based on compact mode.
 */
function buildPayload({
  prompt,
  history,
  systemPrompt,
  model,
  temperature,
  uploadedFiles,
  session_id,
  stop = [],
  compact = false,
  text,                  // ✅ new
  reasoning,             // ✅ new
  max_output_tokens,     // ✅ new
  activeMode,            // ✅ new
}) {
  const historyToSend = Array.isArray(history)
    ? history.slice(-(compact ? COMPACT_HISTORY : MAX_HISTORY))
    : [];

    // --- Agent Mode defaults (you can wire to UI later) ---


return {
    prompt,
    history: historyToSend,
    systemPrompt,
    model,
    temperature,
    uploadedFiles,
    session_id,
    stop,
    text,                 // ✅ include
    reasoning,            // ✅ include
    max_output_tokens,    // ✅ include
    activeMode,           // ✅ include
  };
}

async function abortServerStream({ apiUrl, token, session_id }) {
  const body = JSON.stringify({ session_id });
  for (let i = 0; i < 4; i++) {
    try {
      const r = await fetch(`${apiUrl}/api/chat/abort`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token||""}` },
        body
      });
      const j = await r.json().catch(() => ({}));
      if (j?.aborted) return;            // success
    } catch {}
    await new Promise(r => setTimeout(r, 75)); // small backoff
  }
}

/**
 * Sends a message using SSE and automatically retries with a smaller payload on a 413 error.
 */
async function sendWithSSE({ url, payload, token, onMessage, onDone, onLimit, controllerRef }) {
  let triedCompact = false;
  let ctrl;
  let aborted = false;
  let skipPersistHeader = false;
  
  const onOpenHandler = async (res) => {
   // ----- LIMIT HANDLING (fires before any stream) -----
   const limitHeader = res.headers.get("x-plan-limit-reached");
   const isLimit = res.status === 429 || res.status === 402 || limitHeader === "1";
   if (isLimit) {
    const limit = Number(res.headers.get("x-plan-limit") ?? 5);
     const resetTime = res.headers.get("x-plan-reset") ?? null;
     const plan = res.headers.get("x-plan") ?? "free";
     onLimit?.({ limit, resetTime, plan });
     aborted = true;
     try { ctrl?.abort(); } catch {}
     // Tell caller we aborted due to limit; also skipPersistence in this case
     onDone?.(null, { aborted: true, limitReached: true, skipPersist: true });
     return;
   }
    if (!res.ok) {
      if (res.status === 413) {
        if (!triedCompact) { triedCompact = true; throw new Error("413-RETRY"); }
        throw new Error("Payload is too large, even after compacting.");
      }
      throw new Error(`Server returned an error: HTTP ${res.status}`);
    }
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("text/event-stream")) {
      throw new Error(`Expected SSE stream but got ${ct}. The server might be down or returning an error page.`);
    }
      skipPersistHeader = (res.headers.get("x-vintari-skip-persist") === "1");
  };

  const onMessageHandler = (ev) => {
    if (ev.event === "error") {
      onDone?.(new Error(ev.data || "An error occurred on the server."));
      if (ctrl) try { ctrl.abort(); } catch {}
      return; // stop handling
    }
    if (ev.event === "close") {
      onDone?.();                 // explicit server-sent 'close' event
      if (ctrl) try { ctrl.abort(); } catch {}
      return;
    }
    onMessage?.(ev.data);
  };

  const onErrorHandler = (err) => {
    // IMPORTANT: treat AbortError as a graceful finish
    const msg = String(err?.message || "");
       if (err?.name === "AbortError" || msg.includes("aborted")) {
      aborted = true;
      if (ctrl) try { ctrl.abort(); } catch {}
      onDone?.(null, { aborted: true, skipPersist: skipPersistHeader }); // ✅ pass meta
      return;
    }

    if (ctrl) try { ctrl.abort(); } catch {}
    throw err; // real error
  };

  while (true) {
    if (aborted) break; // don't start another attempt if user stopped
    const attemptPayload = triedCompact ? buildPayload({ ...payload, compact: true }) : buildPayload(payload);

    try {
      ctrl = new AbortController();
      if (controllerRef) controllerRef.current = ctrl;

      await fetchEventSource(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token || ""}` },
        body: JSON.stringify(attemptPayload),
        signal: ctrl.signal,
        onopen: onOpenHandler,
        onmessage: onMessageHandler,
        onerror: onErrorHandler,
      });

      // If we got here, the connection closed normally (no AbortError, no throw).
      if (!aborted) onDone?.(null, { aborted: false, skipPersist: skipPersistHeader }); // ✅ pass meta
      break;

    } catch (e) {
      if (e.message === "413-RETRY") {
        console.warn("Payload too large. Retrying in compact mode…");
        continue;
      }
      onDone?.(e);                  // surface real errors
      throw e;
    } finally {
      if (controllerRef) controllerRef.current = null;
    }
  }
}




// --- Main React Hook ---
export default function useSendMessage({
  conversation,
  setConversation,
  currentChatId,
  fetchChatHistory,
  setCurrentChatId,
  fetchChatSessions,
  setLoading,
  setInput,
  onToken,
  onStreamDone,
  onLimit,        // ✅ NEW
}) {
  const session = useSession();
  const supabase = useSupabaseClient();
    // Streaming control & state
const controllerRef = useRef(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [stoppedByUser, setStoppedByUser] = useState(false);

const stop = async () => {
  setStoppedByUser(true);

  // Flip UI immediately
  setIsStreaming(false);
  setLoading(false);

  // Abort the browser-side SSE
  if (controllerRef.current) {
    try { controllerRef.current.abort(); } catch {}
    controllerRef.current = null;
  }

  // Tell the server to abort the OpenAI stream
  const token = (await supabase.auth.getSession()).data.session?.access_token;
  if (currentChatId && token) {
    abortServerStream({
      apiUrl: "https://vintariai-backend-1.onrender.com",
      token,
      session_id: currentChatId,
    }).catch(() => {});
  }
};



const modeConfig = {
  "Innovation Catalyst": {
    prompt: strategistInstructionsInnovationCatalyst,
    model: "gpt-5",
    reasoning: { effort: "medium" },
    text: { verbosity: "medium" },      // was "long"
    max_output_tokens: 25000,
  },
  "Adaptive Strategist": {
    prompt: strategistInstructionsAdaptive,
    model: "gpt-5-mini",
    reasoning: { effort: "low" },
    text: { verbosity: "medium" },       // was "short"
    max_output_tokens: 25000,
  },
"Strategist": {
  prompt: null,   // no longer static
  model: "gpt-5",
  reasoning: { effort: "medium" },
  text: { verbosity: "low" },
  max_output_tokens: 25000,
},


  "Risk Horizon Scan": {
    prompt: strategistInstructionsBlackSwan,
    model: "gpt-5",
    reasoning: { effort: "medium" },
    text: { verbosity: "medium" },      // was "long"
    max_output_tokens: 25000,
  },

 "Agentic Strategist": {
  prompt: StrategistInstructionsAgentic,
    model: "gpt-5-mini",
    reasoning: { effort: "medium" },
    text: { verbosity: "medium" },
    max_output_tokens: 25000,
  },

 // "Market Shake-Up": {
 //   prompt: strategistInstructionsDisrupt,
 //   model: "gpt-5",
 //   reasoning: { effort: "medium" },
 //   text: { verbosity: "low" },      // was "long"
 //   max_output_tokens: 9000,
 // },
 // "Strategic Alternatives": {
 //   prompt: strategistInstructionsExploreAlt,
 //   model: "gpt-5-mini",
 //   reasoning: { effort: "medium" },
 //   text: { verbosity: "low" },
 //   max_output_tokens: 9000,
///  },
 // "Reality Check": {
 //   prompt: strategistInstructionsValidate,
 //   model: "gpt-5-mini",
 //   reasoning: { effort: "medium" },
 //   text: { verbosity: "low" },       // was "short"
//    max_output_tokens: 9000,
//  },

};


const send = async function handleSend({
  input = "",
  file = null,
  systemPrompt = null,
  visibleUserMessage = null,
  activeMode = "Strategist",
}) {
  const token = await supabase.auth.getSession().then((res) => res.data.session?.access_token);

  if (!token || (!input.trim() && !file) || !session?.user) {
    console.warn("handleSend aborted: Missing token, input/file, or user session.");
    return;
  }

  setLoading(true);
  setIsStreaming(true);
  setStoppedByUser(false);
  // UI: push user's message + assistant placeholder row
  const messageToShow = visibleUserMessage?.startsWith("🧠 Strategy Applied:")
    ? visibleUserMessage
    : (visibleUserMessage ?? input);

  const userMessage = { role: "user", content: messageToShow };
  const assistantPlaceholder = { role: "assistant", content: "" };
  setConversation((prev) => ({ ...prev, messages: [...prev.messages, userMessage, assistantPlaceholder] }));
  setInput("");

  // Optional file upload
  let uploadedFilesPayload = null;
  if (file) {
    try {
      const filePath = `public/${session.user.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage.from("user-uploads").upload(filePath, file);
      if (uploadError) throw uploadError;
      uploadedFilesPayload = [{ path: filePath, name: file.name, type: file.type }];
    } catch (error) {
      console.error("🔥 Supabase upload error:", error.message);
      setConversation((prev) => {
        const newMessages = [...prev.messages];
        newMessages[newMessages.length - 1] = { role: "assistant", content: `❌ Error uploading file: ${error.message}` };
        return { ...prev, messages: newMessages };
      });
      setLoading(false);
      return;
    }
  }

  // Mode config + strategist instructions
  const config = modeConfig[activeMode] || modeConfig["Strategist"];
  const {
    model: selectedModel,
    temperature: selectedTemperature,
    stop: stopSequence,
    text: textConfig,
    reasoning: reasoningConfig,
    max_output_tokens: maxOut,
  } = config;

  // Provocation / intent
  let provocationQuestion = "General critical assumption or blind spot";
  let primaryIntent = "N/A", secondaryIntents = [];
  try {
    const provocationData = runProvocationEngine?.(input, activeMode);
    if (provocationData) {
      provocationQuestion = provocationData.provocationQuestion;
      primaryIntent = provocationData.primaryIntent;
      secondaryIntents = provocationData.secondaryIntents;
    }
  } catch (e) {
    console.warn("Provocation engine error (non-fatal):", e);
  }

  const strategistInstructions = buildStrategistInstructions(provocationQuestion);
  const appendedContent = `
\n\n--- Context Snapshot ---
Primary intent (must anchor provocation): ${primaryIntent}
Secondary intents (optional shading): ${secondaryIntents.join(", ")}
`;

 // ✅ Decide if this turn should enable web search
const useWebSearch = shouldUseWebSearch({ input, primaryIntent });

// ✅ Objective passed to backend so the system message can reference it
const agentObjective = `User goal: ${input}. If information could be stale or time-sensitive, use live web search and include citations.`;

// ✅ Build tools once so logs and payload stay in sync
const tools = buildTools({
  allowWebSearch: AGENT_MODE_DEFAULTS.allowWebSearch,
  useWebSearch,
});

const finalSystemPrompt = `${strategistInstructions}${appendedContent}`;

// --- diagnostics
console.groupCollapsed("Vintari Pre-Send");
console.log("Mode:", activeMode);
console.log("Model:", selectedModel);
console.log("Knobs:", { effort: reasoningConfig?.effort, verbosity: textConfig?.verbosity, maxOut });
console.log("History msgs:", (conversation?.messages?.length ?? 0));
console.log("Prompt chars:", input.length);
console.log("🧠 Primary Intent:", primaryIntent);
console.log("Secondary Intents:", secondaryIntents);
console.log("🤔 Provocation Question:", provocationQuestion);
console.log("System prompt chars:", finalSystemPrompt.length);
console.log("Files:", uploadedFilesPayload?.length || 0);
// NEW: show agent/search decisions
console.log("Agent mode:", AGENT_MODE_DEFAULTS);
console.log("useWebSearch:", useWebSearch);
console.log("tools:", tools);
console.groupEnd();


  // Stream UI plumbing
  let buffer = "";
  let done = false;
  let flushTimer = null;
let fullText = "";
  const flushNow = () => {
    if (!buffer) return;
    const chunk = buffer;
    buffer = "";
    setConversation((prev) => {
      const msgs = [...prev.messages];
      const i = msgs.length - 1; // assistant placeholder is last
      if (i >= 0 && msgs[i].role === "assistant") {
        msgs[i] = { ...msgs[i], content: (msgs[i].content || "") + chunk };
      }
      return { ...prev, messages: msgs };
    });
  };

  const scheduleFlush = (ms = 33) => {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushNow();
      if (!done) scheduleFlush(ms);
    }, ms);
  };

  const clearFlush = () => {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  };

  const dangerousStops = new Set(["Provocation\n", "Provocation"]);
  const safeStop = stopSequence && !dangerousStops.has(stopSequence?.[0]) ? stopSequence : undefined;

const handleStreamEnd = (err, opts = {}) => {
  done = true;
  clearFlush();
  flushNow();

  if (err && !opts.aborted) {
    console.error("SSE stream failed:", err.message);
    setConversation((prev) => {
      const msgs = [...prev.messages];
      if (msgs.length && msgs[msgs.length - 1].role === "assistant") {
        msgs[msgs.length - 1] = {
          ...msgs[msgs.length - 1],
          content: `❌ Error: ${err.message}`,
        };
      }
      return { ...prev, messages: msgs };
    });
  } else {
    console.log("Stream finished successfully.");

    // ✅ Only pull history from DB if the server said we persisted (paid plans)
    const skipPersist = !!opts?.skipPersist;
    if (currentChatId && !skipPersist) {
      fetchChatHistory(currentChatId);
    }
    // If skipPersist===true (free plan), do nothing — keep the streamed text in UI.
  }

  setLoading(false);
  setIsStreaming(false);
};


 try {
    const t0 = performance.now();
    let tFirstChunk = null;
    let totalChars = 0;
    let sourcesAppended = false;
    let metaResult = { aborted: false, limitReached: false };

    await sendWithSSE({
      url: `${"https://vintariai-backend-1.onrender.com"}/api/chat`,
      token,
      payload: {
        prompt: input,
        history: Array.isArray(conversation?.messages)
          ? conversation.messages.slice(0, -1) // drop the assistant placeholder we just pushed
          : [],
        systemPrompt: systemPrompt
          ? `${finalSystemPrompt}\n\n--- Extra System Directives ---\n${systemPrompt}`
          : finalSystemPrompt,
        model: selectedModel,
        temperature: selectedTemperature,
        uploadedFiles: uploadedFilesPayload,
        session_id: currentChatId,
        stop: safeStop,
        text: { verbosity: textConfig?.verbosity || "medium" },
        reasoning: reasoningConfig,
        max_output_tokens: maxOut,
        activeMode,

        // Agent flags + tools + objective
        agent: { enabled: AGENT_MODE_DEFAULTS.enabled },
        tools,
        objective: agentObjective,
      },
      controllerRef,
      onMessage: (raw) => {
        try {
          if (raw === "[DONE]") return;
          const data = JSON.parse(raw);
if (data?.type === "title" && data?.title) {
  // only refresh if the title pertains to the chat you're viewing
  if (!currentChatId || data.session_id === currentChatId) {
    fetchChatSessions?.();
  }
}
          if (!tFirstChunk) tFirstChunk = performance.now();

          if (data?.content) {
            totalChars += data.content.length;
            buffer += data.content;
            fullText += data.content;
            onToken?.(data.content);
            scheduleFlush(33);
          }

          if (data.chatId && !currentChatId) {
            setCurrentChatId?.(data.chatId);
            fetchChatSessions?.();
          }

          // Append sources once
          if (!sourcesAppended && Array.isArray(data?.sources) && data.sources.length) {
            const list = data.sources
              .map((s) => {
                const title = (s.title || s.url || "").replace(/\n/g, " ").trim();
                const url = String(s.url || "").trim();
                return `- [${title}](${url})`;
              })
              .join("\n");

            const sourcesBlock = `\n\n### Sources\n${list}\n`;
            buffer += sourcesBlock;
            fullText += sourcesBlock;
            sourcesAppended = true;
            scheduleFlush(33);
          }
        } catch (e) {
          if (raw !== "[DONE]") console.error("SSE parse error:", raw, e);
        }
      },
onDone: async (err, meta) => {
          const tEnd = performance.now();
        const ttfbMs = tFirstChunk ? (tFirstChunk - t0).toFixed(0) : "N/A";
        const totalMs = (tEnd - t0).toFixed(0);
        const cps = tFirstChunk
          ? (totalChars / ((tEnd - tFirstChunk) / 1000)).toFixed(1)
          : "N/A";
        console.log("Stream metrics:", { ttfbMs, totalMs, totalChars, cps });

        onStreamDone?.(fullText);


// === ZAPIER HANDOFF (only for the agentic lens) ===
try {
  if (activeMode === "Agentic Strategist" || activeMode === "Strategic Execution" || activeMode === "Strategist") {
    const plan = extractActionPlanJSON(fullText);
    if (plan && plan.actions && plan.actions.length) {
      // OPTIONAL: include who/what/when for your Zap’s mappings
 const ok = await sendToZapier(plan, {
          userId: session?.user?.id,
        chatId: currentChatId,
        createdAt: new Date().toISOString(),
        mode: activeMode,
  }, token);
      console.log("Zapier relay:", ok ? "sent" : "failed");
    } else {
      console.log("No Action Plan JSON found; skipping Zapier relay.");
    }
  }
} catch (e) {
  console.warn("Zapier relay error (non-fatal):", e);
}

  try { await fetchChatSessions?.(); } catch {}
        handleStreamEnd(err, meta); // <-- put this back so loading/streaming flags settle
        metaResult = meta || metaResult;
      },
      onLimit: (info) => {
        // surface to hook consumer immediately (UI shows modals)
        onLimit?.(info);
      },
    });

    if (stoppedByUser) {
      setConversation((prev) => {
        const msgs = [...prev.messages];
        const i = msgs.length - 1;
        if (i >= 0 && msgs[i].role === "assistant") {
          msgs[i] = { ...msgs[i], content: (msgs[i].content || "") + " …stopped" };
        }
        return { ...prev, messages: msgs };
      });
    }

    // Let caller know if we aborted/limit so they can decide whether to clear input
    return metaResult;

  } catch (error) {
    console.error("Unexpected SSE error:", error);
    handleStreamEnd(error);
  } finally {
    setLoading(false);
  }
}; // <-- end of send

// If your linter still complains about "unused", keep these 3 no-ops.
// eslint-disable-next-line no-unused-expressions
void isStreaming;
// eslint-disable-next-line no-unused-expressions
void stop;
// eslint-disable-next-line no-unused-expressions
void send;

return { send, stop, isStreaming, stoppedByUser };
} // <-- end of useSendMessage